/**
 * `reporter.ts` — capture uncaught client errors, queue them, and flush them to the server.
 *
 * The delivery half of the subsystem; `queue.ts` is the durable half. Split because they fail
 * independently: the queue survives a dead network, the flusher survives a full localStorage,
 * and neither is allowed to take the app down with it.
 *
 * ── THE RE-ENTRANCY HAZARD, WHICH IS THE REAL RISK HERE ──
 *
 * An error reporter is uniquely able to destroy the app it's observing. If anything inside
 * `captureError` throws, that throw is itself an uncaught error, which re-enters
 * `captureError`, which throws again — an unbounded loop inside the browser's error handler,
 * which is not a place with a stack budget to spare. So the whole capture path runs inside a
 * `reporting` guard and a blanket `try/catch`: a failure to report is silently dropped, always.
 * Never add a code path here that can throw past that guard.
 */
import {
  CLIENT_ERROR_BATCH_CAP,
  CLIENT_ERROR_FIELD_CAP,
  CLIENT_ERROR_PATH,
  type ClientErrorReport,
  type ClientErrorSource,
} from "@lancer-vtt/shared";
import { dropDelivered, enqueue, readQueue } from "./queue";

/** Re-entrancy guard — see the header. Set for the duration of any capture or flush. */
let reporting = false;
/** The authenticated slot, when the app has one. Absent for a pre-login crash. */
let currentSlotId: string | undefined;
/** Whether a flush is in flight, so overlapping triggers don't double-POST the same batch. */
let flushing = false;
/** Set once `install()` has run, so a double-install doesn't double-register listeners. */
let installed = false;

/**
 * Tell the reporter which slot is logged in. Called from the game session on welcome; a
 * module-level setter rather than a React context because the capture path runs *outside*
 * React — a crash during render has no context to read.
 */
export function setErrorReportingSlot(slotId: string | undefined): void {
  currentSlotId = slotId;
}

/** Truncate to the shared field cap, so an enormous stack can't blow the localStorage budget. */
function clamp(value: string | undefined): string | undefined {
  if (value === undefined) return undefined;
  const trimmed = value.trim();
  if (trimmed === "") return undefined;
  return trimmed.length > CLIENT_ERROR_FIELD_CAP ? trimmed.slice(0, CLIENT_ERROR_FIELD_CAP) : trimmed;
}

/**
 * Coerce whatever was thrown into a message + stack. JavaScript lets you throw anything —
 * a string, `undefined`, a DOM event, a plain object — and a reporter that assumes `Error`
 * loses exactly the weird cases that are hardest to debug from the description alone.
 */
function describeThrown(thrown: unknown): { message: string; stack?: string } {
  if (thrown instanceof Error) {
    return { message: `${thrown.name}: ${thrown.message}`, stack: thrown.stack };
  }
  if (typeof thrown === "string") return { message: thrown };
  try {
    return { message: `non-Error thrown: ${JSON.stringify(thrown)}` };
  } catch {
    // A circular or exotic object. `String()` can itself throw on a hostile `toString`.
    return { message: "non-Error thrown (unserializable)" };
  }
}

export type CaptureArgs = {
  source: ClientErrorSource;
  thrown: unknown;
  /** React's `componentStack`, when the capture came from an error boundary. */
  componentStack?: string;
};

/**
 * Record one error: build the report, queue it durably, then try to deliver.
 *
 * Queue-before-send is the ordering that matters — a report is on disk before any network
 * work is attempted, so a page that dies mid-POST still has it for the next load.
 */
export function captureError(args: CaptureArgs): void {
  if (reporting) return;
  reporting = true;
  try {
    const { message, stack } = describeThrown(args.thrown);
    const report: ClientErrorReport = {
      capturedAt: Date.now(),
      source: args.source,
      message: clamp(message) ?? "unknown client error",
      stack: clamp(stack),
      url: clamp(window.location.href),
      userAgent: clamp(navigator.userAgent),
      slotId: currentSlotId,
      componentStack: clamp(args.componentStack),
    };
    enqueue(report);
  } catch {
    // Never let the reporter surface its own failure — see the header.
  } finally {
    reporting = false;
  }
  // Outside the guard: the flush is async and must not hold `reporting` across an await, or a
  // genuine error during the flush window would be silently swallowed instead of recorded.
  void flushClientErrors();
}

/**
 * Deliver the queued reports. Safe to call at any time and from any trigger — it no-ops when
 * the queue is empty or a flush is already running.
 *
 * **Any 2xx clears the batch, including a partial store.** The server caps a batch and drops
 * the overflow; retrying the remainder would need per-report acks, which is more protocol than
 * a crash log is worth. A network failure keeps everything for the next trigger.
 */
export async function flushClientErrors(): Promise<void> {
  if (flushing) return;
  const queued = readQueue();
  if (queued.length === 0) return;
  flushing = true;
  const batch = queued.slice(0, CLIENT_ERROR_BATCH_CAP);
  try {
    const response = await fetch(CLIENT_ERROR_PATH, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ reports: batch }),
      // The reports carry no credential and the endpoint sets no cookie; sending none keeps
      // this request identical whether it fires from a live page or a half-torn-down one.
      credentials: "omit",
      keepalive: true,
    });
    // A 4xx means the server will never accept this batch — dropping it is the only way out of
    // an otherwise permanent retry loop. A 5xx is transient, so it keeps the queue.
    if (response.ok || (response.status >= 400 && response.status < 500)) dropDelivered(batch);
  } catch {
    // Offline or server unreachable — the queue is untouched and the next trigger retries.
  } finally {
    flushing = false;
  }
}

/**
 * Install the global capture handlers and the flush triggers.
 *
 * Call as early as possible in `main.tsx`: errors thrown before this runs are simply not
 * captured, and boot-time crashes are among the most valuable ones.
 *
 * The three flush triggers are chosen to cover the disconnect cases without polling:
 *   - **install** drains whatever the *previous* page load left behind (the crash-then-reload case),
 *   - **`online`** fires the moment the network comes back,
 *   - **`visibilitychange`→visible** covers a laptop resumed from sleep, where `online` may
 *     never fire because the browser never considered itself offline.
 */
export function installErrorReporting(): void {
  if (installed) return;
  installed = true;

  window.addEventListener("error", (event: ErrorEvent) => {
    // `event.error` is the thrown value when there was one; a cross-origin script error gives
    // only the message, and is still worth recording (it usually means an extension or a
    // blocked asset, which is a real explanation for a user's "it just broke").
    captureError({ source: "window.onerror", thrown: event.error ?? event.message });
  });

  window.addEventListener("unhandledrejection", (event: PromiseRejectionEvent) => {
    captureError({ source: "unhandledrejection", thrown: event.reason });
  });

  window.addEventListener("online", () => void flushClientErrors());
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") void flushClientErrors();
  });

  void flushClientErrors();
}
